import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { catchError, EMPTY, exhaustMap, filter, from, map, Observable, of, switchMap } from 'rxjs';
import { ConnectFile, ICustomFileActionButton } from 'trimble-connect-workspace-api';
import { IngestionSource } from '../client-header/client-header.models';
import { isApiErrorType } from '../error-handling/api-error-type';
import { LicenseService } from '../license/license.service';
import { injectLogger } from '../logging/logger-injection';
import { ProjectQuotaService } from '../quota/project-quota.service';
import { SelectOnly } from '../scandata/scandata.actions';
import { PointcloudAPIStatus, ScandataModel } from '../scandata/scandata.models';
import { TenancyService } from '../tenancy/tenancy.service';
import { Role } from '../user/user.models';
import { UserState } from '../user/user.state';
import { ConnectIngestionService } from './connect-ingestion.service';
import { connectPanelTranslations } from './connect-panel.translations';
import { ConnectService } from './connect.service';
import { ConnectFileSelectEvent } from './models/connect-file-select-event';
import { ConnectPanelStatus } from './models/connect-panel-status';
import { ExternalFileId } from './models/external-file-id';

@Injectable({
  providedIn: 'root',
})
export class ConnectPanelService {
  private logger = injectLogger('ConnectPanelService');
  private connectService = inject(ConnectService);
  private ingestionService = inject(ConnectIngestionService);
  private licenseService = inject(LicenseService);
  private projectQuotaService = inject(ProjectQuotaService);
  private store = inject(Store);
  private tenancyService = inject(TenancyService);

  public subscribeToConnectEvents() {
    this.subscribeConnectFileSelect();
    this.subscribeConnectButtonClick();
    this.subscribeConnectFileNameClick();
  }

  private subscribeConnectFileSelect() {
    this.setupFileEvent('extension.fileSelected')
      .pipe(
        switchMap(({ file }) => this.checkRegion(file)),
        switchMap((file) => this.checkLicense(file)),
        switchMap((file) => this.getScan(file)),
        map(({ file, scan }) => this.checkIfFileWasUpdated(file, scan)),
        switchMap(({ file, fileWasUpdated, scan }) =>
          isNil(scan) || fileWasUpdated
            ? of({ file, fileWasUpdated })
            : this.setupPanelForScan(file.id, scan as ScandataModel).pipe(switchMap(() => EMPTY)),
        ),
        switchMap(({ file, fileWasUpdated }) => this.checkAdmin(file, fileWasUpdated)),
        switchMap(({ file, fileWasUpdated }) =>
          this.setupPanel(
            file.id,
            fileWasUpdated
              ? ConnectPanelStatus.ReadyToTileNewVersion
              : ConnectPanelStatus.ReadyToTile,
          ),
        ),
      )
      .subscribe();
  }

  private subscribeConnectButtonClick() {
    this.subscribeConnectButtonViewClick();
    this.subscribeConnectButtonTileClick();
    this.subscribeConnectButtonRefreshClick();
  }

  private subscribeConnectFileNameClick() {
    this.observeConnectEvents('extension.fileViewClicked')
      .pipe(
        map((workspaceEvent) => workspaceEvent.data as ConnectFileSelectEvent),
        filter(
          (fileEvent) =>
            fileEvent.source === 'explorer.FileNameClicked' ||
            fileEvent.source === 'explorer.FileVersionNameClicked',
        ),
        switchMap((fileEvent) => this.getScan(fileEvent.file)),
      )
      .subscribe(({ file, scan }) => {
        if (isDefined(scan)) {
          this.store.dispatch(new SelectOnly([scan.id]));
          this.connectService.goTo3dExtension();
        } else {
          this.connectService.goToConnect3dViewer({ modelId: file.id });
        }
      });
  }

  private subscribeConnectButtonViewClick() {
    this.setupFileEvent('extension.fileViewClicked')
      .pipe(
        filterStatus(ConnectPanelStatus.Viewing),
        switchMap((fileEvent) => this.getIngestion(fileEvent.file)),
        switchMap((ingestion) => this.store.dispatch(new SelectOnly([ingestion.scan.id]))),
      )
      .subscribe(() => this.connectService.goTo3dExtension());
  }

  private subscribeConnectButtonTileClick() {
    this.setupFileEvent('extension.fileViewClicked')
      .pipe(
        filterStatus(ConnectPanelStatus.Tiling, ConnectPanelStatus.TilingError),
        switchMap(({ file }) => this.checkQuota(file)),
        exhaustMap((file) =>
          this.getFileDownloadUrl(file).pipe(
            switchMap(({ file, downloadUrl }) => this.createIngestion(file, downloadUrl)),
            switchMap((file) => this.setupPanel(file.id, ConnectPanelStatus.ReadyToRefresh)),
          ),
        ),
      )
      .subscribe();
  }

  private subscribeConnectButtonRefreshClick() {
    this.setupFileEvent('extension.fileViewClicked')
      .pipe(
        filterStatus(ConnectPanelStatus.Refreshing),
        switchMap((fileEvent) => this.getIngestion(fileEvent.file)),
        switchMap((ingestion) => this.setupPanelForScan(ingestion.file.id, ingestion.scan)),
      )
      .subscribe();
  }

  private setupFileEvent(eventId: string) {
    return this.observeConnectEvents(eventId).pipe(
      map((workspaceEvent) => workspaceEvent.data as ConnectFileSelectEvent),
      filter((fileEvent) => this.fileEventForPanel(fileEvent)),
      switchMap((fileEvent) =>
        this.setupPanel(fileEvent.file.id, ConnectPanelStatus.CheckingStatus).pipe(
          map(() => fileEvent),
        ),
      ),
    );
  }

  private fileEventForPanel(fileEvent: ConnectFileSelectEvent) {
    return (
      fileEvent.source.startsWith('explorer') && fileEvent.source !== 'explorer.FileNameClicked'
    );
  }

  private observeConnectEvents(eventId: string) {
    return from(this.connectService.getWorkspace()).pipe(
      switchMap((workspace) => workspace.event$),
      filter((workspaceEvent) => workspaceEvent.id === eventId),
    );
  }

  private getCustomFileActionButtons(status: ConnectPanelStatus): ICustomFileActionButton[] {
    const translations = connectPanelTranslations();

    switch (status) {
      case ConnectPanelStatus.CheckingStatus:
      case ConnectPanelStatus.Refreshing:
        return [
          {
            button: {
              label: translations.refreshing.button.text,
              disabled: true,
            },
            message: {
              text: translations.refreshing.message.text,
              type: 'info',
            },
          },
        ];
      case ConnectPanelStatus.RegionNotSupported:
        return [
          {
            button: {
              label: translations.readyToTile.button.text,
              disabled: true,
            },
            message: {
              text: translations.regionNotSupported.message.text,
              type: 'info',
            },
          },
        ];
      case ConnectPanelStatus.IngestionError:
        return [
          {
            button: {
              label: translations.ingestionError.button.text,
              onClick: ConnectPanelStatus.Refreshing,
            },
            message: {
              text: translations.ingestionError.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.InsufficientPermission:
        return [
          {
            button: {
              label: translations.insufficientPermission.button.text,
              disabled: true,
            },
            message: {
              text: translations.insufficientPermission.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.NoActiveLicense:
        return [
          {
            button: {
              label: translations.noActiveLicense.button.text,
              disabled: true,
            },
            message: {
              text: translations.noActiveLicense.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.QuotaExceeded:
        return [
          {
            button: {
              label: translations.quotaExceeded.button.text,
              disabled: true,
            },
            message: {
              text: translations.quotaExceeded.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.ReadyToRefresh:
        return [
          {
            button: {
              label: translations.readyToRefresh.button.text,
              onClick: ConnectPanelStatus.Refreshing,
            },
            message: {
              text: translations.readyToRefresh.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.ReadyToTile:
        return [
          {
            button: {
              label: translations.readyToTile.button.text,
              onClick: ConnectPanelStatus.Tiling,
            },
            message: {
              text: translations.readyToTile.message.text,
              type: 'info',
            },
          },
        ];
      case ConnectPanelStatus.ReadyToTileNewVersion:
        return [
          {
            button: {
              label: translations.readyToTileNewVersion.button.text,
              onClick: ConnectPanelStatus.Tiling,
            },
            message: {
              text: translations.readyToTileNewVersion.message.text,
              type: 'info',
            },
          },
        ];
      case ConnectPanelStatus.ReadyToView:
        return [
          {
            button: {
              label: translations.readyToViewIn3d.button.text,
              onClick: ConnectPanelStatus.Viewing,
            },
          },
        ];
      case ConnectPanelStatus.Tiling:
        return [
          {
            button: {
              label: translations.tiling.button.text,
              disabled: true,
            },
            message: {
              text: translations.tiling.message.text,
              type: 'warning',
            },
          },
        ];
      case ConnectPanelStatus.TilingError:
        return [
          {
            button: {
              label: translations.tilingError.button.text,
              onClick: ConnectPanelStatus.TilingError,
            },
            message: {
              text: translations.tilingError.message.text,
              type: 'error',
            },
          },
        ];
      case ConnectPanelStatus.Viewing:
        return [
          {
            button: {
              label: translations.viewing.button.text,
              disabled: true,
            },
            message: {
              text: translations.viewing.message.text,
              type: 'info',
            },
          },
        ];
    }
  }

  private getIngestion(file: ConnectFile) {
    return this.ingestionService
      .getIngestion(file)
      .pipe(catchError((err) => this.handleError(file, err)));
  }

  private getScan(file: ConnectFile) {
    return this.ingestionService.getScanForConnectFile(file).pipe(
      map((scan) => ({ file, scan })),
      catchError((err) => this.handleError(file, err)),
    );
  }

  private getFileDownloadUrl(file: ConnectFile) {
    return this.connectService.getFileDownloadUrl(file).pipe(
      map((download) => ({ file, downloadUrl: download.url })),
      catchError((err) => this.handleError(file, err)),
    );
  }

  private createIngestion(file: ConnectFile, downloadUrl: string) {
    return this.ingestionService
      .createIngestion(file, downloadUrl, IngestionSource.ConnectBrowser)
      .pipe(
        map(() => file),
        catchError((err) => this.handleError(file, err)),
      );
  }

  private checkAdmin(file: ConnectFile, fileWasUpdated: boolean) {
    return this.store
      .selectOnce(UserState.userRole)
      .pipe(
        switchMap((role) =>
          role === Role.Admin || role === Role.FullAccess
            ? of({ file, fileWasUpdated })
            : this.setupPanel(file.id, ConnectPanelStatus.InsufficientPermission).pipe(
                switchMap(() => EMPTY),
              ),
        ),
      );
  }

  private checkIfFileWasUpdated(file: ConnectFile, scan?: ScandataModel) {
    if (isNil(scan)) return { file, fileWasUpdated: false, scan };

    const externalFileIdHash = new ExternalFileId(scan.externalFileId ?? '').hash;

    if (isDefined(externalFileIdHash))
      return { file, fileWasUpdated: externalFileIdHash !== file.hash, scan };

    const fileModifiedOn = new Date(file.modifiedOn ?? 0);
    const scanUploadedDate = new Date(scan.uploadedDate ?? 0);
    const fileWasUpdated = fileModifiedOn > scanUploadedDate && (file.revision ?? 1) > 1;

    return { file, fileWasUpdated, scan };
  }

  private checkRegion(file: ConnectFile) {
    return this.tenancyService
      .isProjectRegionSupported()
      .pipe(
        switchMap((isRegionSupported) =>
          isRegionSupported
            ? of(file)
            : this.setupPanel(file.id, ConnectPanelStatus.RegionNotSupported).pipe(
                switchMap(() => EMPTY),
              ),
        ),
      );
  }

  private checkLicense(file: ConnectFile) {
    return this.licenseService
      .hasActiveLicense()
      .pipe(
        switchMap((hasActiveLicense) =>
          hasActiveLicense
            ? of(file)
            : this.setupPanel(file.id, ConnectPanelStatus.NoActiveLicense).pipe(
                switchMap(() => EMPTY),
              ),
        ),
      );
  }

  private checkQuota(file: ConnectFile) {
    return this.projectQuotaService
      .quotaExceeded(file.size ?? 0)
      .pipe(
        switchMap((quotaExceeded) =>
          quotaExceeded
            ? this.setupPanel(file.id, ConnectPanelStatus.QuotaExceeded).pipe(
                switchMap(() => EMPTY),
              )
            : of(file),
        ),
      );
  }

  private setupPanel(connectFileId: string, status: ConnectPanelStatus, thumbnailUrl?: string) {
    return from(this.connectService.getWorkspace()).pipe(
      switchMap(async (workspace) => {
        const actionButtons = this.getCustomFileActionButtons(status);
        return from(
          workspace.api.ui.addCustomFileAction(
            actionButtons.map((actionButton) => ({
              fileId: connectFileId,
              actionButton: actionButton,
              thumbnailUrl,
            })),
          ),
        );
      }),
    );
  }

  private setupPanelForScan(connectFileId: string, scan: ScandataModel) {
    switch (scan.status) {
      case PointcloudAPIStatus.Initializing:
      case PointcloudAPIStatus.InProgress:
        return this.setupPanel(connectFileId, ConnectPanelStatus.ReadyToRefresh, scan.thumbnailUrl);
      case PointcloudAPIStatus.Ready:
        return this.setupPanel(connectFileId, ConnectPanelStatus.ReadyToView, scan.thumbnailUrl);
      case PointcloudAPIStatus.Failed:
        return this.setupPanel(connectFileId, ConnectPanelStatus.TilingError, scan.thumbnailUrl);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleError(file: ConnectFile, err: any) {
    if (isApiErrorType(err, 'NO_ACCESS')) {
      return this.setupPanel(file.id, ConnectPanelStatus.InsufficientPermission).pipe(
        switchMap(() => EMPTY),
      );
    }

    this.logger.error(`Ingesting error (${file.name})`, {}, err);
    return this.setupPanel(file.id, ConnectPanelStatus.IngestionError).pipe(switchMap(() => EMPTY));
  }
}

function filterStatus(...statuses: ConnectPanelStatus[]) {
  return (source: Observable<ConnectFileSelectEvent>) =>
    source.pipe(
      filter((event) => {
        const status = getPanelStatusFromSource(event.source);
        return statuses.findIndex((x) => x === status) > -1;
      }),
    );
}

function getPanelStatusFromSource(source: string) {
  // source includes the onClick string supplied to setCustomActionButton
  // e.g. explorer.tile
  const replacedSource =
    source === 'explorer' ? ConnectPanelStatus.CheckingStatus : source.replace('explorer.', '');
  return replacedSource as ConnectPanelStatus;
}
